// Public signing page for an external party — the parent, almost always.
// Route: /sign/:token  (no login; the token is scoped to one request + party).
//
// Guided, one document at a time, over page images (pdf.js — no PDF viewer,
// so it works on every phone). The parent owns exactly two things: their
// signature spots, and the PHI release choices. Everything else was filled by
// the coordinator and simply reads as text. Signature spots are highlighted
// on the page where the field map knows them; each is signed by a tap, or all
// at once. A document that asks the signer a question (the LON's agree /
// disagree) is answered before it is signed. The page speaks the request's
// language.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import { t, fill, formTitle, signerLabel, normalizeLang } from '../lib/documents/i18n';
import { assemblePacketData, renderPacketBlob, isGappPacket } from '../lib/documents/generatePacketPdf';
import { renderGappDocument, signatureRects, isUploadForm } from '../lib/documents/gappOverlay';
import { renderInternalPage } from '../lib/documents/GappInternalPages';
import { CRM_EMAIL } from '../lib/org';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
const FN = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/`;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;
const PHI_RELEASED = ['Medical history/diagnoses', 'Physician orders/POC', 'Nursing notes', 'Medications', 'Discharge summaries', 'Therapy records', 'Lab/imaging results', 'GAPP documentation'];
const PHI_SIGNERS = ['Parent / Legal Guardian', 'Patient (18+)', 'Legal Representative / POA'];

export default function PacketSign() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [err, setErr] = useState('');
  const [stage, setStage] = useState('intro');   // intro | doc | done
  const [idx, setIdx] = useState(0);
  const [name, setName] = useState('');           // adopted signature
  const [adopting, setAdopting] = useState(null); // { then: fn } while the adopt sheet is open
  const [busy, setBusy] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const lang = normalizeLang(state.packet?.language);
  const L = t(lang);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);
  async function load() {
    try {
      const r = await fetch(`${FN}get-packet-by-token?token=${token}`, { headers: { Authorization: `Bearer ${ANON}`, apikey: ANON } });
      const j = await r.json();
      if (!r.ok) { setErr(j.error || ''); setState({ loading: false }); return; }
      const files = {};
      Object.entries(j.files || {}).forEach(([id, b64]) => { try { files[id] = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)); } catch (_) { /* skip */ } });
      setState({ loading: false, ...j, files });
      const already = (j.slots || []).find((s) => s.signed_name);
      if (already) setName(already.signed_name);
    } catch (e) { setErr(e.message); setState({ loading: false }); }
  }

  const { packet, client: realClient, forms = [], slots = [], allSlots = [], tokens = [], files = {}, recipientName } = state;
  const client = realClient || (packet ? { first_name: packet.subject || packet.title || '', last_name: '', client_id: '' } : null);
  const gapp = isGappPacket(packet?.packet_type);
  const mine = (fk) => slots.filter((s) => s.form_key === fk);

  // Documents in request order. A private packet is one typeset document.
  const docs = useMemo(() => {
    if (!packet) return [];
    if (!gapp) return [{ key: '__all', title: L.packetLabel?.[packet.packet_type] || 'Documents', slots }];
    return forms.map((f) => ({ key: f.form_key, title: isUploadForm(f.form_key) ? (f.form_title || f.source_name) : formTitle(lang, f.form_key, f.form_title), slots: mine(f.form_key) }));
    // eslint-disable-next-line
  }, [packet, forms, slots, lang]);
  const totalSigs = slots.length;
  const signedN = slots.filter((s) => s.signed_at).length;
  const phiForm = forms.find((f) => f.form_key === 'phi_release');
  const hasChoices = !!phiForm && mine('phi_release').length > 0;

  // ── questions placed for this signer (the LON's agree / disagree) ──
  // { group: [values] } per document, from its choice fields for our party.
  const party = slots[0]?.party_role;
  const questionsFor = (fk) => {
    const f = forms.find((x) => x.form_key === fk);
    const q = {};
    ((f?.field_data || {}).fields || []).filter((x) => x.kind === 'choice' && x.party === party && x.group).forEach((x) => { (q[x.group] = q[x.group] || []).push(x.value); });
    return q;
  };
  const [answers, setAnswers] = useState({});   // { form_key: { group: value } }
  const answerOf = (fk) => ({ ...(slots.find((x) => x.form_key === fk && x.choices)?.choices || {}), ...(answers[fk] || {}) });
  const unanswered = (fk) => Object.keys(questionsFor(fk)).filter((g) => !answerOf(fk)[g]);

  // ── signing ──
  // A question left unanswered stops the signing before it starts — before
  // the name is even asked for — and takes the signer to it.
  const answeredFirst = (toSign) => {
    const open = toSign.find((sl) => unanswered(sl.form_key).length);
    if (!open) return true;
    setErr(L.sign.agreeFirst); setIdx(Math.max(0, docs.findIndex((d) => d.key === open.form_key))); setStage('doc');
    return false;
  };
  const signSlots = async (toSign, signedName) => {
    if (!answeredFirst(toSign)) return;
    setBusy('sign'); setErr('');
    const nowIso = new Date().toISOString();
    try {
      for (const slot of toSign) {
        const choices = Object.keys(questionsFor(slot.form_key)).length ? answerOf(slot.form_key) : undefined;
        const r = await fetch(`${FN}submit-signature`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ANON}`, apikey: ANON }, body: JSON.stringify({ token, slotId: slot.id, signedName, choices }) });
        const j = await r.json();
        if (!r.ok || !j.success) throw new Error(j.error || 'Sign failed');
        const signed = (s) => (s.id === slot.id ? { ...s, signed_name: signedName, signed_at: nowIso, ...(choices ? { choices } : {}) } : s);
        setState((st) => ({ ...st, slots: st.slots.map(signed), allSlots: st.allSlots.map(signed) }));
      }
    } catch (e) { setErr(fill(L.sign.signFailed, { msg: e.message })); }
    setBusy('');
  };
  const withName = (then) => { if (name.trim()) then(name.trim()); else setAdopting({ then }); };
  const signOne = (slot) => { if (answeredFirst([slot])) withName((n) => signSlots([slot], n)); };
  const signRemaining = () => { const rest = slots.filter((s) => !s.signed_at); if (answeredFirst(rest)) withName((n) => signSlots(rest, n)); };

  // ── PHI choices ──
  const phi = phiForm?.field_data || {};
  const savePhi = async (patch) => {
    const next = { ...phi, ...patch };
    setState((st) => ({ ...st, forms: st.forms.map((f) => (f.form_key === 'phi_release' ? { ...f, field_data: next } : f)) }));
    try {
      const r = await fetch(`${FN}submit-signature`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ANON}`, apikey: ANON }, body: JSON.stringify({ token, action: 'save_fields', formKey: 'phi_release', fields: patch }) });
      if (!r.ok) throw new Error((await r.json()).error || 'Save failed');
      setSavedNote(L.sign.saved); setTimeout(() => setSavedNote(''), 1500);
    } catch (e) { setErr(e.message); }
  };

  if (state.loading) return <Shell><p>{L.sign.loading}</p></Shell>;
  if (err && !state.slots) return <Shell><h2>{L.sign.linkUnavailable}</h2><p style={{ color: 'var(--text3)' }}>{err || L.sign.linkInvalid}</p></Shell>;

  const patient = `${client?.first_name || ''} ${client?.last_name || ''}`.trim();
  const parentName = recipientName || [client?.guardian_first_name, client?.guardian_last_name].filter(Boolean).join(' ') || '';
  const docWord = docs.length === 1 ? L.sign.docWordOne : L.sign.docWordMany;
  const cur = docs[idx];
  const curSigned = cur ? cur.slots.every((s) => s.signed_at) : true;
  const allSigned = signedN === totalSigs && totalSigs > 0;

  return (
    <Shell wide={stage === 'doc'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div><div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 18 }}>{L.sign.brand}</div><div style={{ color: 'var(--text3)', fontSize: 12 }}>{L.sign.subtitle} · {patient}</div></div>
        {stage === 'doc' && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{signedN} / {totalSigs} {L.sign.signed.toLowerCase()}</div>}
      </div>
      {stage === 'doc' && <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, marginBottom: 10 }}><div style={{ width: `${totalSigs ? (signedN / totalSigs) * 100 : 0}%`, height: '100%', background: 'var(--accent)', borderRadius: 2, transition: 'width .3s' }} /></div>}
      {err && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 8, borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{err}</div>}

      {/* ── intro ── */}
      {stage === 'intro' && (
        <div>
          <h2 style={{ margin: '8px 0 6px', fontSize: 22 }}>{fill(L.sign.hello, { name: parentName || '' })}</h2>
          <p style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.6 }}>{fill(L.sign.intro, { n: docs.length, docWord, patient, sigs: totalSigs, choices: hasChoices ? L.sign.introChoices : '' })}</p>
          <ol style={{ paddingLeft: 20, color: 'var(--text2)', fontSize: 14, lineHeight: 1.8 }}>
            {docs.map((d) => <li key={d.key}>{d.title}{d.slots.length ? <span style={{ color: 'var(--text3)' }}> — {d.slots.length} {d.slots.length === 1 ? L.sign.sigWordOne : L.sign.sigWordMany}</span> : null}</li>)}
          </ol>
          <button onClick={() => setStage('doc')} style={bigBtn}>{allSigned ? L.sign.allDone : L.sign.start} →</button>
          <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 14 }}>{L.sign.questions} <a href={`mailto:${CRM_EMAIL}`}>{CRM_EMAIL}</a>.</p>
        </div>
      )}

      {/* ── one document ── */}
      {stage === 'doc' && cur && (
        <div>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 8 }}>
            {docs.map((d, i) => {
              const done = d.slots.length > 0 && d.slots.every((s) => s.signed_at);
              return <button key={d.key} onClick={() => setIdx(i)} style={{ flexShrink: 0, font: 'inherit', fontSize: 12, padding: '4px 10px', borderRadius: 14, border: '1px solid var(--border)', cursor: 'pointer', background: i === idx ? 'var(--accent)' : done ? 'var(--success-bg)' : 'var(--bg2)', color: i === idx ? '#fff' : done ? 'var(--success)' : 'var(--text2)' }}>{done && i !== idx ? '✓ ' : ''}{i + 1}</button>;
            })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>{fill(L.sign.docOf, { i: idx + 1, n: docs.length })}</div>
          <h2 style={{ margin: '2px 0 10px', fontSize: 18 }}>{cur.title}</h2>

          {cur.key === 'phi_release' && hasChoices && (
            <div style={panel}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{L.sign.phiHead}</div>
              <p style={{ fontSize: 12.5, color: 'var(--text2)', margin: '0 0 10px' }}>{fill(L.sign.phiIntro, { patient })}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 4 }}>
                {PHI_RELEASED.map((o) => (
                  <label key={o} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, padding: '6px 4px' }}>
                    <input type="checkbox" style={{ width: 18, height: 18, accentColor: 'var(--accent)' }} checked={(phi.phiReleased || []).includes(o)}
                      onChange={(e) => savePhi({ phiReleased: e.target.checked ? [...(phi.phiReleased || []), o] : (phi.phiReleased || []).filter((x) => x !== o) })} />
                    {L.phi.released[o] || o}
                  </label>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 10 }}>
                <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>{L.sign.phiValidLabel}
                  <select value={phi.phiValid || 'Until end of home care services'} onChange={(e) => savePhi({ phiValid: e.target.value })} style={input}>
                    <option value="Until end of home care services">{L.phi.validUntilEnd}</option><option value="Other">{L.phi.other.replace(':', '')}</option>
                  </select>
                </label>
                {phi.phiValid === 'Other' && <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>{L.sign.phiValidOtherLabel}<input value={phi.phiValidOther || ''} onChange={(e) => savePhi({ phiValidOther: e.target.value })} style={input} /></label>}
                <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>{L.sign.phiSignerLabel}
                  <select value={phi.phiSignerType || 'Parent / Legal Guardian'} onChange={(e) => savePhi({ phiSignerType: e.target.value })} style={input}>
                    {PHI_SIGNERS.map((o) => <option key={o} value={o}>{L.phi.signerTypes[o] || o}</option>)}
                  </select>
                </label>
              </div>
              {savedNote && <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 6 }}>✓ {savedNote}</div>}
            </div>
          )}

          {Object.keys(questionsFor(cur.key)).includes('agreement') && (
            <div style={{ ...panel, borderColor: unanswered(cur.key).length ? 'var(--accent)' : 'var(--border)' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{L.sign.agreeHead}</div>
              <p style={{ fontSize: 12.5, color: 'var(--text2)', margin: '0 0 10px' }}>{L.sign.agreeIntro}</p>
              <div role="radiogroup" aria-label={L.sign.agreeHead} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {questionsFor(cur.key).agreement.map((v) => {
                  const on = answerOf(cur.key).agreement === v;
                  return (
                    <button key={v} type="button" role="radio" aria-checked={on} disabled={curSigned || !!busy}
                      onClick={() => { setErr(''); setAnswers((a) => ({ ...a, [cur.key]: { ...(a[cur.key] || {}), agreement: v } })); }}
                      style={{ ...smBtn, flex: '1 1 160px', background: on ? 'var(--accent)' : 'var(--card-bg)', color: on ? '#fff' : 'var(--text)', borderColor: on ? 'var(--accent)' : 'var(--border2)', cursor: curSigned ? 'default' : 'pointer' }}>
                      {on ? '✓ ' : ''}{v === 'agree' ? L.sign.agree : L.sign.disagree}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <DocumentPages packet={packet} client={client} forms={forms} slots={allSlots} tokens={tokens} files={files} docKey={cur.key} gapp={gapp} mySlots={cur.slots} lang={lang} L={L} onSign={signOne} />

          <div style={panel}>
            {cur.slots.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>{L.sign.nothingToSign}</div> : cur.slots.map((s) => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13.5 }}>{fill(L.sign.signSpot, { label: signerLabel(lang, s.slot_key, s.signer_label) })}</div>
                {s.signed_at ? <span style={{ color: 'var(--text)', fontFamily: 'cursive', fontSize: 18 }}>{s.signed_name} ✓</span>
                  : <button disabled={!!busy} onClick={() => signOne(s)} style={{ ...smBtn, background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' }}>{L.sign.signHere}</button>}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button onClick={() => (idx === 0 ? setStage('intro') : setIdx(idx - 1))} style={smBtn}>{L.sign.back}</button>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!allSigned && totalSigs - signedN > 1 && <button disabled={!!busy} onClick={signRemaining} style={smBtn}>{fill(L.sign.signAllRemaining, { n: totalSigs - signedN })}</button>}
              {idx < docs.length - 1 && !allSigned
                ? <button onClick={() => setIdx(idx + 1)} style={{ ...smBtn, background: curSigned ? 'var(--accent)' : 'var(--card-bg)', color: curSigned ? '#fff' : 'var(--text2)', borderColor: curSigned ? 'var(--accent)' : 'var(--border2)' }}>{L.sign.nextDoc}</button>
                : <button disabled={!allSigned} onClick={() => setStage('done')} style={{ ...smBtn, background: allSigned ? 'var(--success)' : 'var(--border)', color: '#fff', borderColor: 'transparent' }}>{L.sign.finish}</button>}
              {idx < docs.length - 1 && allSigned && <button onClick={() => setIdx(idx + 1)} style={smBtn}>{L.sign.nextDoc}</button>}
            </div>
          </div>
        </div>
      )}

      {/* ── done ── */}
      {stage === 'done' && (
        <div>
          <div style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: 14, borderRadius: 8, fontWeight: 600, marginBottom: 12 }}>{L.sign.doneBanner}</div>
          <p style={{ fontSize: 14, color: 'var(--text2)' }}>{fill(L.sign.finishSummary, { sigs: signedN, n: docs.length, docWord })}</p>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}><div style={{ color: 'var(--text)', fontFamily: 'cursive', fontSize: 24 }}>{name}</div><div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{L.sign.appliedTo} {docs.filter((d) => d.slots.length).map((d) => d.title).join(' · ')}</div></div>
          <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16 }}>{L.sign.questions} <a href={`mailto:${CRM_EMAIL}`}>{CRM_EMAIL}</a>.</p>
        </div>
      )}

      {/* ── adopt sheet ── */}
      {adopting && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }} onMouseDown={(e) => e.target === e.currentTarget && setAdopting(null)}>
          <div style={{ background: 'var(--card-bg)', width: '100%', maxWidth: 560, borderRadius: '14px 14px 0 0', padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{L.sign.adoptTitle}</div>
            <p style={{ fontSize: 13, color: 'var(--text2)', margin: '4px 0 12px' }}>{L.sign.adoptBody}</p>
            <input autoFocus placeholder={L.sign.namePlaceholder} value={name} onChange={(e) => setName(e.target.value)} style={{ ...input, fontSize: 17, padding: 12 }} />
            <div style={{ height: 44, display: 'flex', alignItems: 'center', fontFamily: 'cursive', fontSize: 28, color: 'var(--accent)', margin: '8px 2px' }}>{name.trim()}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setAdopting(null)} style={smBtn}>{L.sign.back.replace('← ', '')}</button>
              <button disabled={!name.trim()} onClick={() => { const fn = adopting.then; setAdopting(null); fn(name.trim()); }} style={{ ...smBtn, background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' }}>{L.sign.adoptApply}</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ── page images with the signer's spots highlighted ─────────────────────────
function DocumentPages({ packet, client, forms, slots, tokens, files, docKey, gapp, mySlots, lang, L, onSign }) {
  const [pages, setPages] = useState([]);   // [{ w, h, dataUrl }]
  const [busy, setBusy] = useState(true);
  const seq = useRef(0);
  const data = useMemo(() => assemblePacketData({ packet, client, forms, slots, tokens, files }), [packet, client, forms, slots, tokens, files]);
  const rects = useMemo(() => (gapp ? signatureRects(data, docKey, mySlots.map((s) => s.slot_key)) : []), [data, docKey, mySlots, gapp]);

  const render = useCallback(async () => {
    const mine = ++seq.current;
    setBusy(true);
    try {
      let bytes;
      if (gapp) bytes = await renderGappDocument(data, docKey, { renderInternal: (kind) => renderInternalPage(kind, data) });
      else bytes = new Uint8Array(await (await renderPacketBlob({ packet, client, forms, slots, tokens, files })).arrayBuffer());
      if (!bytes) { setPages([]); return; }
      const doc = await pdfjs.getDocument({ data: bytes }).promise;
      const out = [];
      const scale = Math.min(2, (window.devicePixelRatio || 1) * 1.2);
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const vp = page.getViewport({ scale });
        const canvas = document.createElement('canvas'); canvas.width = vp.width; canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        out.push({ w: vp.width, h: vp.height, pw: vp.width / scale, ph: vp.height / scale, dataUrl: canvas.toDataURL('image/jpeg', 0.85) });
      }
      if (mine === seq.current) setPages(out);
    } catch (e) { console.error('render', e); }
    finally { if (mine === seq.current) setBusy(false); }
  }, [data, docKey, gapp, packet, client, forms, slots, tokens, files]);
  useEffect(() => { const h = setTimeout(render, 150); return () => clearTimeout(h); }, [render]);

  const slotById = Object.fromEntries(mySlots.map((s) => [s.slot_key, s]));
  return (
    <div style={{ position: 'relative', margin: '0 0 10px' }}>
      {busy && <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, background: 'var(--bg2)', color: 'var(--text3)', fontSize: 12, padding: '4px 8px', borderRadius: 6 }}>{L.sign.loading}</div>}
      {pages.map((p, pi) => (
        <div key={pi} style={{ position: 'relative', width: '100%', aspectRatio: `${p.pw} / ${p.ph}`, background: '#fff', border: '1px solid var(--border2)', borderRadius: 4, marginBottom: 8, overflow: 'hidden' }}>
          <img src={p.dataUrl} alt={`page ${pi + 1}`} style={{ width: '100%', height: '100%', display: 'block' }} />
          {rects.filter((r) => r.page === pi).map((r, ri) => {
            const s = slotById[r.slot]; if (!s) return null;
            const done = !!s.signed_at;
            const left = `${(r.x / p.pw) * 100}%`, top = `${((p.ph - r.y - r.h) / p.ph) * 100}%`, w = `${(r.w / p.pw) * 100}%`, h = `${(r.h / p.ph) * 100}%`;
            return (
              <button key={ri} type="button" disabled={done} onClick={() => onSign(s)} title={done ? L.sign.signed : L.sign.signHere}
                style={{ position: 'absolute', left, top, width: w, height: h, minHeight: 16, border: `2px solid ${done ? 'var(--success)' : 'var(--accent)'}`, background: done ? 'rgba(13,160,106,.08)' : 'rgba(26,58,107,.14)', borderRadius: 3, cursor: done ? 'default' : 'pointer', padding: 0 }}>
                {!done && <span style={{ position: 'absolute', left: -2, top: -18, background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, whiteSpace: 'nowrap' }}>{L.sign.signHere}</span>}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Shell({ children, wide }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', justifyContent: 'center', padding: '16px 10px 40px' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: '20px 18px', maxWidth: wide ? 760 : 620, width: '100%', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }}>{children}</div>
    </div>
  );
}
const panel = { border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--bg2)', margin: '0 0 10px' };
const input = { width: '100%', padding: 9, border: '1px solid var(--border2)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', font: 'inherit', background: 'var(--card-bg)', color: 'var(--text)' };
const smBtn = { font: 'inherit', fontSize: 14, fontWeight: 600, padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--card-bg)', color: 'var(--text2)', cursor: 'pointer' };
const bigBtn = { ...smBtn, width: '100%', fontSize: 16, padding: 14, background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)', marginTop: 8 };
